import usePageState from '../../hooks/use-page-state';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { Modes } from '../../types/page-modes';
import { ActionColumn } from '../../components/table/columns';
import { Content } from '../../components/layout/content';
import * as Page from '../../components/content/page-header';
import { Icons } from '../../components/icons/icons';
import { Actions, Content as DialogContent, Dialog, Title } from '../../components/dialog/dialog';
import { Label, TextInputLarge } from '../../components/forms/input';
import { useQuery } from '../../hooks/use-query';
import { Tenant } from './types/tenant';
import { useForm, FieldValues } from 'react-hook-form';
import * as Button from '../../components/button/button';
import { PaginatedTable } from '../../components/table/paginated-table';
import React, { useState } from 'react';
import { CreatableSelect, Option } from '../../components/forms/creatable-select';

export const Tenants = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, setValue, formState: { errors }, clearErrors } = useForm();
  const tenants = useQuery<Tenant[]>({ path: '/tenants/all' });
  const postMutation = usePostMutation<Tenant>({ path: '/tenants', cacheKey: '/tenants/all' });
  const putMutation = usePutMutation<Tenant>({ path: `/tenants/${pageState.item?.id as string}`, cacheKey: '/tenants/all' });
  const deleteMutation = useDeleteMutation<Tenant>({ cacheKey: '/tenants/all' });
  const [existingRoles, setExistingRoles] = useState<Option[]>([]);

  const onSubmit = (fields: FieldValues): void => {
    const roles = fields.roles.split(',');
    pageState.mode === Modes.CREATE ? postMutation.mutate({ ...fields, roles }) : putMutation.mutate({ ...fields, roles });
    setPageState(Modes.BROWSE);
  };

  const addTenant = (): void => {
    setExistingRoles([]);
    reset();
    register('roles', { required: 'You must provide a value for Roles.' });
    setPageState(Modes.CREATE);
  };

  const onDeleteConfirm = (): void => {
    setPageState(Modes.BROWSE);
    deleteMutation.mutate({ path: `/tenants/${pageState.item?.id as string}` });
  };

  const onUpdate = (item: Tenant): void => {
    for (const key in item) {
      const value = item[key as keyof Tenant];
      if (key === 'roles') {
        setExistingRoles(roles.filter(item => value.includes(item.value)));
        setValue('roles', roles.map((option: Option) => option.value).toString());
        register('roles', { required: 'You must provide a value for Roles.' });
        continue;
      }
      {setValue(key, value);}
    }
    setPageState(Modes.UPDATE, item);
  };

  const onSelectChange = (name: string, options: Option[]): void => {
    setValue(name, options.map((option: Option) => option.value).toString());
    if (options.length) {
      clearErrors('roles');
    }
  };

  const columns = [
    { accessor: 'id', Header: 'Id' },
    { accessor: 'name', Header: 'Name' },
    { accessor: 'roles', Header: 'Roles' },
    { accessor: 'authentication', Header: 'Authentication' },
    { accessor: 'api', Header: 'API' },
    ActionColumn({
      onDelete: (item: Tenant): void => setPageState(Modes.DELETE, item),
      onUpdate
    })
  ];

  return <>
    <Content>
      <Page.Header>
        <Page.Title>Tenants</Page.Title>
        <Page.SubTitle>
          <span />
          <div>
            <Button.LightBlackSmall onClick={addTenant}>
              <Icons.Add />
              Add Tenant
            </Button.LightBlackSmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      <PaginatedTable isLoading={tenants.isLoading} columns={columns} data={{ content: tenants.data } || []} />
    </Content>
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>{pageState.mode === Modes.CREATE ? 'Add Tenant' : 'Edit Tenant'}</Title>
          <DialogContent>
            <div className="mb-4">
              <Label name="name">Id</Label>
              <TextInputLarge {...register('id', { required: 'You must provide a value for Id.' })} errors={errors} placeholder="Id" disabled={pageState.mode === Modes.UPDATE} />
            </div>
            <div className="mb-4">
              <Label name="name">Name</Label>
              <TextInputLarge {...register('name', { required: 'You must provide a value for Name.' })} errors={errors} placeholder="Name" />
            </div>
            <div className="mb-4">
              <Label>Roles</Label>
              <CreatableSelect name="roles" defaultValue={existingRoles} options={roles} errors={errors} onChange={onSelectChange} />
            </div>
            <div className="mb-4">
              <Label name="authentication">Authentication</Label>
              <TextInputLarge {...register('authentication', { required: 'You must provide a value for Authentication.' })} errors={errors} placeholder="Authentication" />
            </div>
            <div className="mb-4">
              <Label name="api">API</Label>
              <TextInputLarge {...register('api', { required: 'You must provide a value for API.' })} errors={errors} placeholder="API" />
            </div>
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Tenant</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};

const roles = [
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Super Admin', value: 'SUPER_ADMIN' },
];
